import { Component, computed, effect, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconModule, ISelectOption } from '@insolution/core-data-access';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'lib-multi-select',
  imports: [NgbDropdownModule, IconModule, TranslatePipe],
  templateUrl: './multi-select.html',
  styleUrl: './multi-select.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelect),
      multi: true,
    },
  ],
})
export class MultiSelect implements ControlValueAccessor {
  public disabled = input<boolean>(false);
  public placeholder = input<string>('');
  public appearance = input<'form-control' | 'button'>('form-control');

  protected options = signal<ISelectOption[]>([]);
  protected isDisabled = signal<boolean>(false);

  protected selectedOptions = computed(() =>
    this.options()
      .filter((option) => option.selected)
      .map((option) => option.label)
      .join(', ')
  );

  protected hasValue = computed(() => this.options().some((option) => option.selected));

  private onChange: (options: ISelectOption[] | null | undefined) => void = () => {
    /* intentionally empty */
  };
  private onTouched: () => void = () => {
    /* intentionally empty */
  };

  writeValue(options: ISelectOption[] | null | undefined): void {
    this.options.set(options ?? []);
  }

  registerOnChange(fn: (options: ISelectOption[] | null | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected onOptionToggle(option: ISelectOption): void {
    if (this.isDisabled() || option.disabled) {
      return;
    }

    const currentOptions = this.options();

    const newOptions = currentOptions.map((opt) => {
      if (opt.value === option.value) {
        return { ...opt, selected: !opt.selected };
      }
      return opt;
    });

    this.options.set(newOptions);
    this.onChange(newOptions);
  }
}
